/**
 * Hareketli SVG ikonların ortak kabuğu (#229, #233).
 *
 * - Renk `currentColor`'dan gelir: ikon, verilen Tailwind `text-*` class'ıyla
 *   boyanır (hardcoded hex yok).
 * - Animasyonlar `tailwind.config.js` → `icon*` keyframe'leridir ve hep
 *   `motion-safe:` ile verilir: işletim sisteminde "hareketi azalt" açıksa
 *   ikon durur.
 * - Tetik (`trigger`):
 *     "always" → sürekli oynar (dashboard çipleri, uyarı kartları)
 *     "hover"  → yalnız `group` sınıflı ata üzerine gelinince oynar (menü)
 *     "off"    → hareketsiz
 * - Dönen/sallanan parçalar `[transform-box:fill-box]` ile kendi kutusuna göre
 *   döner; aksi halde SVG'nin (0,0) noktası etrafında savrulurlar.
 */

// Tailwind sınıfları derleme anında kaynak metinden toplanır — birleştirilmiş
// string üretilemez. Bu yüzden her animasyonun iki tetik sınıfı burada AÇIK yazılı.
const ANIM = {
  iconSwing: { always: "motion-safe:animate-iconSwing", hover: "motion-safe:group-hover:animate-iconSwing" },
  iconPing: { always: "motion-safe:animate-iconPing", hover: "motion-safe:group-hover:animate-iconPing" },
  iconBlink: { always: "motion-safe:animate-iconBlink", hover: "motion-safe:group-hover:animate-iconBlink" },
  iconRise: { always: "motion-safe:animate-iconRise", hover: "motion-safe:group-hover:animate-iconRise" },
  iconWrench: { always: "motion-safe:animate-iconWrench", hover: "motion-safe:group-hover:animate-iconWrench" },
  iconBounce: { always: "motion-safe:animate-iconBounce", hover: "motion-safe:group-hover:animate-iconBounce" },
  iconDash: { always: "motion-safe:animate-iconDash", hover: "motion-safe:group-hover:animate-iconDash" },
  iconDrain: { always: "motion-safe:animate-iconDrain", hover: "motion-safe:group-hover:animate-iconDrain" },
  iconSpin: { always: "motion-safe:animate-iconSpin", hover: "motion-safe:group-hover:animate-iconSpin" },
  iconDraw: { always: "motion-safe:animate-iconDraw", hover: "motion-safe:group-hover:animate-iconDraw" },
  iconPop: { always: "motion-safe:animate-iconPop", hover: "motion-safe:group-hover:animate-iconPop" },
  iconWiggle: { always: "motion-safe:animate-iconWiggle", hover: "motion-safe:group-hover:animate-iconWiggle" },
  iconFly: { always: "motion-safe:animate-iconFly", hover: "motion-safe:group-hover:animate-iconFly" },
  iconDrop: { always: "motion-safe:animate-iconDrop", hover: "motion-safe:group-hover:animate-iconDrop" },
  iconOrbit: { always: "motion-safe:animate-iconOrbit", hover: "motion-safe:group-hover:animate-iconOrbit" },
  iconFlip: { always: "motion-safe:animate-iconFlip", hover: "motion-safe:group-hover:animate-iconFlip" },
  iconFloat: { always: "motion-safe:animate-iconFloat", hover: "motion-safe:group-hover:animate-iconFloat" },
  iconGrow: { always: "motion-safe:animate-iconGrow", hover: "motion-safe:group-hover:animate-iconGrow" },
};

/** Gecikme sınıfları — sıralı (dalga gibi) hareket için; tetikten bağımsız */
export const DELAY = {
  d1: "[animation-delay:150ms]",
  d2: "[animation-delay:300ms]",
  d3: "[animation-delay:450ms]",
};

/**
 * @param {"always"|"hover"|"off"} trigger
 * @param {keyof typeof ANIM} name
 * @param {string} [delay] - DELAY.* sınıfı
 */
export const anim = (trigger, name, delay = "") => {
  const cls = ANIM[name]?.[trigger];
  if (!cls) return "";
  return delay ? `${cls} ${delay}` : cls;
};

/** Eski `animated` bayrağıyla yeni `trigger`'ı uzlaştırır */
export const resolveTrigger = (trigger, animated = true, fallback = "always") =>
  trigger || (animated ? fallback : "off");

/** Döndürülen/ölçeklenen parçaların kendi kutusu etrafında hareket etmesi için */
export const FILL_BOX = { transformBox: "fill-box" };

export default function AnimatedSvg({
  className = "w-6 h-6",
  title,
  children,
  viewBox = "0 0 24 24",
}) {
  return (
    <svg
      viewBox={viewBox}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`overflow-visible ${className}`}
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      {children}
    </svg>
  );
}
